import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faAddressBook } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Customer } from 'src/app/models/customer';
import { Rights } from 'src/app/models/enums/rights';
import { ICustomer } from 'src/app/models/interfaces/customer.interface';
import { CustomersService } from 'src/app/services/customers.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { CustomerDetailContext } from './customer-detail-context';
import { CustomerDetailComponent } from './customer-detail/customer-detail.component';
import { CustomersListComponent } from './customers-list/customers-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-customers',
    templateUrl: './customers.component.html',
    styleUrls: ['./customers.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, CustomersListComponent, DetailHeaderComponent, CustomerDetailComponent],
})
export class CustomersComponent extends DefaultManagementComponent<ICustomer, Customer, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faCustomer = faAddressBook;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;
    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre clients.
     */
    public defaultContext: CustomerDetailContext = new CustomerDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param CustomersService : Service métier utilisé pour gérer les clients.
     */
    constructor(
        protected override notificationService: NotificationService,
        private CustomersService: CustomersService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.CustomerCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.CustomerEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un client.
     * @param customer Client selectionné.
     */
    public onCustomerSelected(customer: Customer) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadCustomer(customer.id).subscribe(
            (x) => {
                this.refresh(Customer.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le client." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param customer client sélectionné.
     */
    public onCustomerDisplayed(customer: Customer) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadCustomer(customer.id).subscribe(
            (x) => {
                this.refresh(Customer.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le client." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param customer client sélectionné.
     */
    public onCustomerEdited(customer: Customer) {
        this.spinner.show('main-spinner');
        this.createContext();
        // Cas de la mise à jour.
        this.loadCustomer(customer.id).subscribe(
            (x) => {
                this.refresh(Customer.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer le client." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onCustomerCreated() {
        this.displayForm(Customer.createFormCopy(new Customer()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param customer client sélectionné.
     */
    public onCustomerDuplicated(customer: Customer) {
        let duplicatedCustomer = Customer.createFormCopy(customer);
        duplicatedCustomer.id = null;
        this.displayForm(duplicatedCustomer, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param Customer : Valeur du client retourné par le formulaire.
     */
    public customerUpdated(customer: Customer): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.CustomersService.update(this.context.editedItemId, customer).subscribe(
                (x) => {
                    this.refreshWithNotification(Customer.createFormCopy(x), 'Client mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le client.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.CustomersService.add(customer).subscribe(
                (x) => {
                    this.refreshWithNotification(Customer.createFormCopy(x), 'Client créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le client.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param customer client sélectionné.
     */
    public onCustomerDeleted(customer: Customer) {
        this.spinner.show('main-spinner');
        // Suppression effective du client.
        this.CustomersService.delete(customer.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Client supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer le client.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onCustomersExported() {
        this.createContext();
        this.itemsList.export('Clients.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onCustomersImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onCustomersHelped() {
        this.itemsList.displayHelpDialog('customers');
    }

    /**
     * Charge les données d'un client.
     * @param CustomerId : Id du client à charger.
     */
    public loadCustomer(customerId: number): Observable<Customer> {
        return this.CustomersService.get(customerId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param Customer : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(Customer: ICustomer): CustomerDetailContext {
        let context = this.createContext();
        context.formContext.item = Customer;
        context.editedItemId = Customer.id;
        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): CustomerDetailContext {
        let context = new CustomerDetailContext();
        return context;
    }
}
